<template>
	<div class="MediaControls">
		<div class="MediaControls-seek-container">
			<div class="MediaControls-seek" :class="timeline ? 'MediaControls-seekTimeline' : ''">
				<div
					class="MediaControls-seekElapsed"
					:style="{ transform: 'translateX(-' + (100 - progressPercent) + '%)' }"
					aria-hidden="true"
				></div>
				<label v-bind:for="'seek-' + assetId" class="is-visuallyHidden">{{ messages.seek }}</label>
				<input
					:id="'seek-' + assetId"
					class="MediaControls-seekInput"
					type="range"
					min="0"
					:max="duration"
					step="0.01"
					:aria-valuenow="progressPercent"
					@input="setPosition"
					@change="setTime"
				/>
			</div>
		</div>

		<div class="MediaControls-controls" :class="controlsClasses" ref="controls">
			<button class="Button MediaControls-playButton" type="button" :aria-label="messages.play" @click="$emit('togglePlay')">
				<!--use icons component here-->
				<span>
					<CloIcon :symbol="playing ? 'pause' : 'play'" />
				</span>
			</button>

			<!--for volume icon-->
			<div class="MediaControls-volume" v-if="!hideVolume" v-on:mouseenter="showVolume = true" v-on:mouseleave="showVolume = false">
				<button class="Button Button--transparent Button--small MediaControls-volumeButton" type="button" @click="toggleVolume">
					<span>
						<CloIcon :symbol="volumeSlider == 0 ? 'audioMute' : 'audio'" />
					</span>
					<span class="is-visuallyHidden">{{ messages.mute }}</span>
				</button>

				<!--volume slider-->
				<transition name="MediaControls-volumeSliderOverlay">
					<div class="MediaControls-volumeSliderOverlay" v-show="showVolume">
						<label class="is-visuallyHidden" v-bind:for="'volume-' + assetId">{{ messages.volume }}</label>
						<div class="Slider">
							<input
								:id="'volume-' + assetId"
								type="range"
								min="0"
								max="1"
								step=".1"
								v-model="volumeSlider"
								@input="setVolume"
								aria-label="Volume"
								:aria-valuenow="volumeSlider"
							/>
						</div>
					</div>
				</transition>
			</div>

			<span class="MediaControls-time" v-if="!hideTime">
				<span>{{ formatTime(currentTime) }}</span> /
				<span>{{ formatTime(duration) }}</span>
			</span>

			<div class="MediaControls-meta" v-if="title || user || location || date">
				<h3 v-if="title" class="MediaControls-title">
					{{ title }}
				</h3>
				<ul class="UnorderedList--flat u-mbn">
					<li v-if="user">©&nbsp; {{ user }}</li>
					<li v-if="location">{{ location }}</li>
					<li v-if="date">{{ date }}</li>
					<li v-if="citationUrl">
						<a :href="citationUrl" rel="noopener" target="_blank">Macaulay Library {{ citationName }}</a>
					</li>
				</ul>
			</div>

			<div class="MediaControls-zoom" v-if="zoom">
					<button 
						class="MediaControls-zoomButton"
						type="button" 
						@click="resetZoom"
						aria-label="reset zoom"
					>
						<CloIcon symbol="search" size="sm" />
					</button>
					<button
						class="MediaControls-zoomButton"
						type="button"
						@click="zoomOut"
						aria-label="zoom out"
					>
						<CloIcon symbol="minus" />
					</button>
					<button
						class="MediaControls-zoomButton"
						type="button"
						@click="zoomIn"
						aria-label="zoom in"
					>
						<CloIcon symbol="plus" />
					</button>
				<label v-bind:for="'zoom-' + assetId" class="is-visuallyHidden">Zoom</label>
				<div class="Slider u-showForMedium">
					<input
						:id="'zoom-' + assetId"
						type="range"
						v-model="zoomSlider"
						min="1"
						max="100"
						step="1"
						:aria-valuenow="zoomSlider"
						@input="$emit('set-zoom', zoomSlider)"
					/>
				</div>
			</div>

			<div class="MediaControls-group MediaControls-group--right">
				<div v-if="animatedText.length" class="MediaControls-info">
					{{ animatedText }}
				</div>
				<div class="MediaControls-frameStep" v-if="frameStep">
					<div class="ButtonGroup u-inline-sm">
						<button type="button" class="Button Button--small" :aria-label="messages.previous" @click="previousFrame">
							<CloIcon symbol="arrowLeft" />
						</button>
						<button type="button" class="Button Button--small" :aria-label="messages.next" @click="nextFrame">
							<CloIcon symbol="arrowRight" />
						</button>
					</div>
				</div>

				<div class="MediaControls-hd" :class="{ 'is-active': hdActive }" v-if="hdAvailable">
					<button type="button" v-on:click="$emit('toggleHd')">HD</button>
				</div>

				<div class="MediaControls-fullscreen" v-if="showFullscreen">
					<button type="button" :aria-label="messages.fullscreen" v-on:click="$emit('toggleFullscreen')">
						<CloIcon symbol="fullscreen" />
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import CloButton from '@/base/CloButton/CloButton.vue';
import CloUnorderedList from '@/base/CloUnorderedList/CloUnorderedList.vue';
import CloSlider from '@/forms/CloSlider/CloSlider.vue';
import CloIcon from '@/icons/CloIcon/CloIcon.vue';

export default {
	components: { CloButton, CloUnorderedList, CloSlider, CloIcon },
	data() {
		return {
			showVolume: false,
			volumeSlider: 1,
			volumeToRestore: 1,
			zoomSlider: this.baseZoomLevel,
		};
	},
	props: {
		/**
		 * This group of classes refer to the entirety of control buttons/functions
		 */
		controlsClasses: String,
		/**
		 * Shows whether the media is playing or not
		 */
		playing: Boolean,
		/**
		 *An array of messages to convey during or after an action such as mute, seek
		 */
		messages: Object,
		/**
		 * Shows whether the HD option is present or not. Present only if media is a video
		 */
		hdAvailable: {
			type: Boolean,
			default: false,
		},
		/**
		 * Color of HD button
		 */
		hdActive: {
			type: Boolean,
			default: false,
		},
		/**
		 * This control feature toggles between different sizes of the media window (maximize or minimize)
		 */
		showFullscreen: {
			type: Boolean,
			default: false,
		},
		citationUrl: {
			default: false,
		},
		specimenUrl: {
			default: false,
		},
		/**
		 * Asset ID
		 */
		assetId: String,
		progressPercent: Number,
		/**
		 * This hides time feature or displays it
		 */
		hideTime: {
			type: Boolean,
			default: false,
		},
		/**
		 * This hides or displays the volume feature
		 */
		hideVolume: {
			type: Boolean,
			default: false,
		},
		/**
		 * How many frames to move per split of a second
		 */
		frameStep: {
			type: Number,
			default: 0,
		},
		currentTime: {
			type: Number,
			default: 0,
		},
		duration: {
			type: Number,
			default: 0,
		},
		animatedText: {
			type: String,
			default: '',
		},
		title: {
			type: String,
			default: null,
		},
		user: {
			type: String,
			default: null,
		},
		location: {
			type: String,
			default: null,
		},
		date: {
			type: String,
			default: null,
		},
		citationName: {
			type: String,
			default: null,
		},
		timeline: {
			type: Boolean,
			default: false,
		},
		zoom: {
			type: Boolean,
			default: false,
		},
		baseZoomLevel: {
			type: Number,
			default: 50,
            validator: value => {
                // Only accepts floats in range [1, 100]
                return (!isNaN(value) && value >= 1 && value <= 100);
            },
		},
	},

	mounted() {
		document.addEventListener('lichen:resize', this.setSize);
		document.addEventListener('lichen:video-togglePlay', this.handleTogglePlayEvent);
		document.addEventListener('lichen:video-pause', this.pause);
		document.addEventListener('lichen:video-destroy', this.removeListeners);
	},
	beforeDestroy() {
		this.removeListeners();
	},
	methods: {
		removeListeners() {
			document.removeEventListener('lichen:resize', this.setSize);
			document.removeEventListener('lichen:video-togglePlay', this.handleTogglePlayEvent);
			document.removeEventListener('lichen:video-pause', this.pause);
			document.removeEventListener('lichen:video-destroy', this.removeListeners);
		},
		handleTogglePlayEvent(e) {
			if (e.detail.assetId === parseInt(this.assetId)) {
				this.togglePlay();
			}
		},

		togglePlay() {
			if (this.$refs.video) {
				if (this.playing) {
					this.$refs.video.pause();
				} else {
					this.$refs.video.play();
				}
			}
		},
		setVolume(event) {
			//this.$refs.video.volume = this.volumeSlider;
			//this.volumeToRestore = this.volumeSlider;

			this.$emit('setVolume', event.target.value);
			this.volumeToRestore = event.target.value;
		},
		//make setPosition and setTime as customized events
		setPosition() {
			this.$emit('setPosition', event.target.value);
		},
		setTime() {
			this.$emit('setTime', event.target.value);
		},
		toggleVolume() {
			if (this.volumeSlider == 0) {
				if (this.volumeToRestore == 0) {
					this.volumeSlider = 1;
				} else {
					this.volumeSlider = this.volumeToRestore;
				}
			} else {
				this.volumeSlider = 0;
			}
			//this.setVolume();
			this.$emit('setVolume', this.volumeSlider);
		},
		previousFrame() {
			this.$emit('previousFrame');
		},
		nextFrame() {
			this.$emit('nextFrame');
		},
		// time values should be formatted as 0:00 or 0:00:00
		formatTime(time) {
			if (time < 3600) {
				return Math.floor(time / 60) + ':' + ('0' + Math.floor(time % 60)).slice(-2);
			}
			return (
				Math.floor(time / 3600) +
				':' +
				('0' + Math.floor((time % 3600) / 60)).slice(-2) +
				':' +
				('0' + Math.floor(time % 60)).slice(-2)
			);
		},
		zoomOut() {
			this.zoomSlider = parseInt(this.zoomSlider);
			if (this.zoomSlider > 11) {
				this.zoomSlider -= 10;
			} else {
				this.zoomSlider = 1;
			}
			this.$emit('set-zoom', this.zoomSlider);
		},
		zoomIn() {
			this.zoomSlider = parseInt(this.zoomSlider);
			if (this.zoomSlider < 90) {
				this.zoomSlider += 10;
			} else {
				this.zoomSlider = 100;
			}
			this.$emit('set-zoom', this.zoomSlider);
		},
		resetZoom() {
			this.zoomSlider = this.baseZoomLevel;
			this.$emit('set-zoom', this.zoomSlider);
		},
	},
};
</script>

<style lang="scss">
$mediaSeekHeight: 0.5rem;
$mediaSeekHeightHover: 0.5rem;
$mediaSeekTimelineHeight: 3rem;

.MediaControls {
	display: block;
}

.MediaControls-controls {
	display: flex;
	width: 100%;
	align-items: center;
	background-color: $color-black;
	color: $color-text-reverse;
	font-size: $text-size-xs;
	@include inset-xs;
	button {
		margin-bottom: 0;
	}
}
.MediaControls-playButton {
	@include inset-squish-md;
	background-color: $color-primary-3;
	.Icon {
		@include icon-md;
	}
	&:hover,
	&:focus {
		background-color: $color-primary-4;
	}
}
.MediaControls-time {
	@include inset-squish-sm;
	min-width: 6.25rem;
	text-align: center;
	@include inline-sm;
}
.MediaControls-volume {
	position: relative;
	display: none;
	@include breakpoint(medium) {
		display: flex;
		align-items: center;
		margin-left: $space-md;
	}
	.MediaControls-volumeSliderOverlay {
		position: absolute;
		left: 100%;
		@include inset-squish-md;
		background-color: $color-black;
		vertical-align: middle;
		width: 7rem;
		&-enter-active {
			transition: opacity 0.2s ease;
		}
		&-leave-active {
			transition: opacity 0.1s ease;
		}
		&-enter-to,
		&-leave {
			opacity: 1;
		}
		&-enter,
		&-leave-to {
			opacity: 0;
		}
	}
	.MediaControls-volumeButton {
		.Icon {
			@include icon-sm;
			fill: $color-white;
		}
	}
	.MediaControls-volumeSlider {
		max-width: 4rem;
	}
}

.MediaControls-toggleButton {
	color: $color-text-reverse;
	font-weight: normal;
	&.is-active {
		color: $color-link-reverse;
	}
	&:hover {
		background-color: $color-neutral-5;
	}
}

// zoom
.MediaControls-zoom {
	@include inset-squish-sm;
	display: inline-flex;
	align-items: center;
	border-radius: $radius-sm;
	//border: 1px solid darken($color-neutral-4, 10%);
	background-color: $color-neutral-5;
	gap: $space-xs;
	label {
		font-size: $text-size-xs;
		color: $color-text-reverse;
		display: inline;
		@include inline-sm;
	}
	input[type='range'] {
		max-width: 4rem;
		@include inline-sm;
	}
	.MediaControls-zoomButton {
		padding: 1px 3px;
		color: $color-text-reverse;
		font-weight: normal;
		border-radius: $radius-sm;
		&:hover {
			background-color: lighten($color-neutral-5, 10%);
			color: $color-text-reverse;
		}
		.Icon {
			fill: currentColor;
			@include icon-sm;
		}
	}
}

// the "seek" slider and progress bar
.MediaControls-seek-container {
	position: relative;
	height: $mediaSeekHeight;
}
.MediaControls-seekInput {
	width: 100%;
	position: absolute;
	left: 0px;
	bottom: 0px;
	padding: 0;
	height: $mediaSeekHeight;
	background: transparent;
	-webkit-appearance: none;
	overflow: hidden;
	cursor: pointer;
	outline: 0;
}
.MediaControls-seekElapsed {
	height: $mediaSeekHeight;
	position: absolute;
	background-color: $color-primary-2;
	transition: height 0.2s ease;
	width: 100%;
	will-change: transform;
	&:after {
		content: '';
		display: block;
		width: $mediaSeekHeight;
		height: $mediaSeekHeight;
		border: 0;
		background-color: $color-black;
		position: absolute;
		left: 100%;
		top: 0;
		margin-left: calc(-1 * #{$mediaSeekHeight} / 2);
		transition: all 0.2s ease;
	}
}
.MediaControls-seek {
	width: 100%;
	height: $mediaSeekHeight;
	background-color: $color-neutral-3;
	position: absolute;
	bottom: 0;
	transition: height 0.2s ease;
	&.MediaControls-seekTimeline {
		.MediaControls-seekInput {
			height: $mediaSeekTimelineHeight;
		}
		&:hover,
		&:active {
			height: $mediaSeekHeight;
			.MediaControls-seekInput {
				height: $mediaSeekTimelineHeight;
			}
			.MediaControls-seekElapsed {
				height: $mediaSeekHeight;
				&:after {
					height: $mediaSeekHeight;
					width: $mediaSeekHeight;
					margin-left: calc(-1 * #{$mediaSeekHeight} / 2);
				}
			}
		}
	}
}

// browser specific: hide track and thumb
.MediaControls-seekInput::-moz-range-track {
	width: 100%;
	height: 100%;
	-moz-appearance: none;
	margin: 0px;
	padding: 0px;
	border: 0 none;
	background: transparent;
	color: transparent;
	overflow: visible;
}
.MediaControls-seekInput::-ms-track {
	width: 100%;
	height: 100%;
	-webkit-appearance: none;
	margin: 0px;
	padding: 0px;
	border: 0 none;
	background: transparent;
	color: transparent;
	overflow: visible;
}
.MediaControls-seekInput::-webkit-slider-thumb {
	-webkit-appearance: none;
	height: $mediaSeekHeightHover;
	width: $mediaSeekHeightHover;
	background-color: transparent;
}
.MediaControls-seekInput::-moz-range-thumb {
	height: $mediaSeekHeightHover;
	width: $mediaSeekHeightHover;
	background-color: transparent;
	border: 0;
}
.MediaControls-seekInput::-ms-thumb {
	height: $mediaSeekHeightHover;
	width: $mediaSeekHeightHover;
	background-color: transparent;
}
.MediaControls-seekInput::-ms-fill-lower {
	background: transparent;
	border: 0 none;
}
.MediaControls-seekInput::-ms-fill-upper {
	background: transparent;
	border: 0 none;
}
.MediaControls-seekInput::-ms-tooltip {
	display: none;
}

.MediaControls-group {
	display: flex;
	align-items: center;
}
.MediaControls-group--right {
	margin-left: auto;
}

// ml logo
.MediaControls-branding {
	@include inline-sm;
	a {
		@include inset-sm;
		display: inline-block;
		border-radius: $radius-sm;
		&:hover {
			background-color: $color-neutral-5;
		}
	}
}

.MediaControls-hd {
	button {
		@include inset-squish-sm;
		font-size: 1.25rem;
		font-weight: bold;
		border-radius: $radius-sm;
		&:hover {
			background-color: $color-neutral-5;
		}
	}
	&.is-active {
		button {
			color: $color-link-reverse;
		}
	}
}
.MediaControls-fullscreen {
	button {
		@include inset-squish-sm;
		border-radius: $radius-sm;
		&:hover {
			background-color: $color-neutral-5;
		}
	}
}
.MediaControls-info {
	@include inline-sm;
	font-size: $text-size-md;
	&:empty {
		display: none;
	}
}

.MediaControls-meta {
	@include stack-sm;
	.MediaControls-title {
		@include text(4);
        color: $color-text-reverse;
	}
	ul li {
		@include text(2, loose);
		font-weight: normal;
	}
}

.MediaControls .MediaControls-controls {
	input[type='range'] {
		background: transparent;
		margin: 0;
		display: block;
	}
	// reverse slider styles for Webkit
	input[type=range]::-webkit-slider-runnable-track {
   		background: $color-neutral-4;
	}
	// reverse slider styles for Firefox
	input[type='range']::-moz-range-track {
		border: 0;
	}
	input[type='range']::-moz-range-thumb {
		border-color: $color-neutral-2;
	}
}

/* ----------------
	Variants
---------------- */
// Reverse seek container (light)
.MediaControls-seek-container--reverse {
	.MediaControls-seek {
		background-color: $color-neutral-1;
	}
	.MediaControls-seekElapsed {
		background-color: $color-primary-3;
	}
}

// Reverse controls (light)
.MediaControls-controls--reverse {
	background-color: $color-neutral-0;
	color: $color-text;
	.MediaControls-playButton {
		background-color: $color-link;
		&:hover,
		&:active,
		&:focus {
			background-color: $color-link-hover;
		}
	}
	.MediaControls-volumeButton {
		.Icon {
			fill: $color-text;
		}
	}
	.MediaControls-branding {
		a:hover {
			background-color: $color-neutral-1;
		}
	}
	.MediaControls-hd {
		button:hover {
			background-color: $color-neutral-1;
		}
		&.is-active {
			button {
				color: $color-link;
			}
		}
	}
	.MediaControls-fullscreen {
		button:hover {
			background-color: $color-neutral-1;
		}
	}
}
</style>
